import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

import { api, isConnectionError } from "@/lib/api";
import { useAccessibility } from "./AccessibilityContext";

const saveError = (err) =>
  toast.error(isConnectionError(err) ? "Die Verbindung ist unterbrochen. Deine Änderung wurde nicht gespeichert – bitte erneut versuchen." : "Die Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.");

// Own profile + prefs from the Core. Every change is confirmed only after the server accepted it.
export function useMemberProfile() {
  const { a11y, setA11y, resetA11y } = useAccessibility();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const active = useRef(false);
  const readId = useRef(0);
  const writing = useRef(false);

  const read = useCallback(async () => {
    if (!active.current) return false;
    const id = ++readId.current;
    setError(false);
    try {
      const { data: d } = await api.get("/member/profile");
      if (!active.current || id !== readId.current) return false;
      if (!d?.profile || typeof d.profile !== "object" || Array.isArray(d.profile)) throw new Error("Invalid profile response");
      const p = d.profile.prefs || {};
      const chosen = p.accessibility_choices || {};
      const next = {};
      ["high_contrast", "large_text", "reduce_motion"].forEach((k) => { if (chosen[k] === true) next[k] = !!(p.accessibility || {})[k]; });
      setData(d);
      if (Object.keys(next).length) setA11y(next);
      return true;
    } catch (_) {
      if (active.current && id === readId.current) setError(true);
      return false;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual reloads cannot race a pending write; the write performs its own fresh read.
  const load = useCallback(() => writing.current ? Promise.resolve(false) : read(), [read]);
  useEffect(() => {
    active.current = true;
    read();
    return () => { active.current = false; readId.current += 1; };
  }, [read]);

  const prefs = (data && data.profile.prefs) || { notifications: true, accessibility: {} };
  const privacy = { discoverable: prefs.privacy?.discoverable !== false, allow_contact_requests: prefs.privacy?.allow_contact_requests !== false };
  const canWrite = () => active.current && !writing.current;

  const patch = async (body, okText = "Gespeichert.") => {
    if (!canWrite()) return false;
    writing.current = true;
    readId.current += 1;
    setSaving(true);
    try {
      await api.patch("/member/profile", body);
      if (!active.current) return false;
      await read();
      if (!active.current) return false;
      toast.success(okText);
      return true;
    } catch (err) {
      if (active.current) saveError(err);
      return false;
    } finally {
      writing.current = false;
      if (active.current) setSaving(false);
    }
  };

  const savePrefs = (next, okText) => patch({ prefs: next }, okText);
  const savePrivacy = (key, val) => savePrefs({ ...prefs, privacy: { ...privacy, [key]: val } });
  const setAcc = async (key, val) => {
    if (!canWrite()) return false;
    const prev = !!a11y[key];
    setA11y({ [key]: val });
    const ok = await savePrefs({ ...prefs, accessibility: { ...a11y, [key]: val }, accessibility_choices: { ...(prefs.accessibility_choices || {}), [key]: true } });
    if (!ok && active.current) setA11y({ [key]: prev });
    return ok;
  };
  const resetAcc = async () => {
    if (!canWrite()) return false;
    const ok = await savePrefs({ ...prefs, accessibility: {}, accessibility_choices: {} }, "Die Darstellung folgt wieder den Einstellungen deines Geräts.");
    if (ok && active.current) resetA11y();
    return ok;
  };
  // Several display choices at once (V3 "Darstellung übernehmen"); reverted when the Core rejects them.
  const applyAcc = async (next) => {
    if (!canWrite()) return false;
    const prev = { ...a11y };
    setA11y(next);
    const chosen = Object.fromEntries(Object.keys(next).map((k) => [k, true]));
    const ok = await savePrefs({ ...prefs, accessibility: { ...a11y, ...next }, accessibility_choices: { ...(prefs.accessibility_choices || {}), ...chosen } }, "Darstellung übernommen.");
    if (!ok && active.current) setA11y(prev);
    return ok;
  };

  return { data, error, saving, load, prefs, privacy, a11y, patch, savePrefs, savePrivacy, setAcc, resetAcc, applyAcc };
}
